import type { AstrolabeId, BallState, ShotInput, TeamId, Vec2 } from "@scb/protocol";
import { createRng } from "./rng";
import {
  fanGeometry,
  type AstrolabeSpec,
  type FanGeometry,
  type TableSpec,
} from "./table";
import * as v from "./vec2";

export interface SimBall {
  id: number;
  team: TeamId;
  pos: Vec2;
  vel: Vec2;
  state: BallState;
  /** 포획된 Astrolabe (state === "captured"일 때만) */
  astrolabe: AstrolabeId | null;
  /** 포획된 슬롯 인덱스 */
  slot: number | null;
  /** 남은 사이드 스핀 (m/s²). 시간에 따라 감쇠한다. */
  spin: number;
  /**
   * 이 공이 이미 득점했는지. 안착한 공은 나중 공에 맞아 빠질 수 있고 다시 안착할 수도
   * 있는데, 그때마다 점수를 주면 같은 공으로 무한히 득점할 수 있다. 그래서 점수는
   * **공마다 한 번만** 인정한다.
   */
  scored: boolean;
  /**
   * 남은 재안착 금지 시간 (s). 칸에서 빠져나온 직후 곧바로 같은 칸에 다시 들어가
   * "맞았는데 아무 일도 안 일어난" 것처럼 보이는 걸 막는다.
   */
  captureCooldown: number;
}

export type SimEvent =
  | {
      type: "capture";
      tick: number;
      ballId: number;
      astrolabe: AstrolabeId;
      slot: number;
      /** 이 공의 첫 안착인지. false면 재안착이라 점수를 주지 않는다. */
      first: boolean;
    }
  /** 안착해 있던 공이 맞아서 빠져나옴 */
  | { type: "release"; tick: number; ballId: number; astrolabe: AstrolabeId; slot: number }
  | { type: "gutter"; tick: number; ballId: number }
  | { type: "stop"; tick: number; ballId: number }
  | { type: "ball_hit"; tick: number; a: number; b: number; impact: number }
  | { type: "wall_hit"; tick: number; ballId: number; impact: number }
  /** 판 몸통(호·현)이나 페그에 부딪힘 */
  | { type: "comb_hit"; tick: number; ballId: number; astrolabe: AstrolabeId; impact: number };

export interface SimulateArgs {
  table: TableSpec;
  /** 현재 테이블 위 공들. 이 배열은 변경되지 않는다 (내부에서 복제). */
  balls: readonly SimBall[];
  /** 발사 입력 */
  shot: ShotInput;
  /** 새로 발사되는 공의 id */
  ballId: number;
  team: TeamId;
  /** 결정론 시드 */
  seed: number;
  /** 리플레이 프레임 샘플링 (Hz). 기본 30 */
  sampleHz?: number;
  /** 시뮬레이션 스텝 (s). 기본 1/120. 바꾸면 결과가 달라진다. */
  dt?: number;
  /** 최대 시뮬레이션 시간 (s). 기본 12 */
  maxSeconds?: number;
}

export interface SimResult {
  /** 시뮬레이션 종료 후 공 상태 (입력 balls + 새 공) */
  balls: SimBall[];
  events: SimEvent[];
  /** [ballId, x, y] 삼중항이 평탄하게 이어진 프레임들 */
  frames: number[][];
  sampleHz: number;
  /** 진행된 틱 수 */
  ticks: number;
  /** maxSeconds에 걸려 강제 종료됐는지 */
  truncated: boolean;
}

const DEFAULT_DT = 1 / 120;
const DEFAULT_SAMPLE_HZ = 30;
const DEFAULT_MAX_SECONDS = 25;

/**
 * 한 서브스텝에서 공이 움직일 수 있는 최대 거리 (공 반지름 대비).
 * 이 값이 작을수록 빠른 공이 격벽·턱·다른 공을 뚫고 지나가는 터널링이 사라진다.
 */
const SUBSTEP_TRAVEL_RATIO = 0.4;
const MAX_SUBSTEPS = 8;

/** 칸에서 빠져나온 공이 다시 안착할 수 없는 시간 (s). */
const RELEASE_COOLDOWN_S = 0.25;

/** 받침 판정용 접촉 여유 (m). */
const SUPPORT_EPS = 0.002;
/** 접촉 방향이 이만큼 아래를 향해야 "받쳐준다"고 본다 (법선 y 성분). */
const SUPPORT_NORMAL_Y = 0.35;

interface Plate {
  spec: AstrolabeSpec;
  geometry: FanGeometry;
}

function slotKey(astrolabe: AstrolabeId, slot: number): string {
  return `${astrolabe}:${slot}`;
}

function cloneBall(b: SimBall): SimBall {
  return {
    id: b.id,
    team: b.team,
    pos: { x: b.pos.x, y: b.pos.y },
    vel: { x: b.vel.x, y: b.vel.y },
    state: b.state,
    astrolabe: b.astrolabe,
    slot: b.slot,
    spin: b.spin,
    scored: b.scored,
    captureCooldown: b.captureCooldown,
  };
}

/** 발사 입력을 공 하나로 변환한다. 각도 0 = +y(업슬로프) 정면. */
export function createShotBall(
  table: TableSpec,
  ballId: number,
  team: TeamId,
  shot: ShotInput,
  seed: number,
): SimBall {
  const rng = createRng(seed);
  const jitter = table.shotJitter;
  const angle = shot.angle + (jitter === 0 ? 0 : rng.signed() * jitter);
  const power = shot.power * (jitter === 0 ? 1 : 1 + rng.signed() * jitter);

  return {
    id: ballId,
    team,
    pos: { x: shot.x, y: table.playLineY },
    vel: { x: Math.sin(angle) * power, y: Math.cos(angle) * power },
    state: "in_play",
    astrolabe: null,
    slot: null,
    spin: shot.spin,
    scored: false,
    captureCooldown: 0,
  };
}

/**
 * 한 번의 샷을 끝까지 시뮬레이션한다.
 *
 * 결정론 보장: 같은 (table, balls, shot, seed, dt) → 항상 같은 결과.
 * `Math.random()`, `Date.now()`를 쓰지 않고 순회 순서도 배열 인덱스로 고정한다.
 *
 * Astrolabe는 당구대 쿠션처럼 **물리 실체**다. 부채꼴 판의 현(아래 변)이 막혀 있어
 * 올라오는 공은 통과할 수 없고, 판보다 넓은 양옆 통로로 돌아 올라간 뒤 경사를 타고
 * 내려와 호의 페그 사이 슬롯에 안착해야 한다. 안착한 공도 나중 공에 맞으면 빠져나온다.
 */
export function simulateShot(args: SimulateArgs): SimResult {
  const { table, shot, ballId, team, seed } = args;
  const dt = args.dt ?? DEFAULT_DT;
  const sampleHz = args.sampleHz ?? DEFAULT_SAMPLE_HZ;
  const maxTicks = Math.ceil((args.maxSeconds ?? DEFAULT_MAX_SECONDS) / dt);
  const sampleEvery = Math.max(1, Math.round(1 / (dt * sampleHz)));

  const balls: SimBall[] = args.balls.map(cloneBall);
  balls.push(createShotBall(table, ballId, team, shot, seed));

  const r = table.ballRadius;
  const events: SimEvent[] = [];
  const frames: number[][] = [];

  // 판 구조물을 미리 계산해둔다 (매 서브스텝마다 다시 만들면 낭비).
  const plates: Plate[] = table.astrolabes.map((spec) => ({
    spec,
    geometry: fanGeometry(spec, r),
  }));

  // 이미 점유된 슬롯 — 포획 판정에 쓰인다.
  const occupied = new Set<string>();
  for (const b of balls) {
    if (b.state === "captured" && b.astrolabe !== null && b.slot !== null) {
      occupied.add(slotKey(b.astrolabe, b.slot));
    }
  }

  let tick = 0;
  let truncated = false;

  /** 정적 물체(턱·격벽·벽)에 반사시킨다. n은 물체 → 공 방향의 단위벡터. */
  const reflect = (b: SimBall, nx: number, ny: number): number => {
    const vn = b.vel.x * nx + b.vel.y * ny;
    if (vn >= 0) return 0;
    const e = table.wallRestitution;
    b.vel.x -= (1 + e) * vn * nx;
    b.vel.y -= (1 + e) * vn * ny;
    return Math.abs(vn);
  };

  const integrate = (sdt: number): void => {
    for (const b of balls) {
      if (b.state !== "in_play") continue;
      const speed = v.len(b.vel);

      let ax = 0;
      // 뉴턴의 사과 — 테이블 어디서든 항상 같은 힘으로 아래로 당겨진다.
      let ay = -table.slopeAccel;

      if (speed > 0) {
        const dir = { x: b.vel.x / speed, y: b.vel.y / speed };
        ax -= dir.x * table.rollingFriction;
        ay -= dir.y * table.rollingFriction;
        if (b.spin !== 0) {
          const p = v.perp(dir);
          ax += p.x * b.spin;
          ay += p.y * b.spin;
        }
      }

      b.vel.x += ax * sdt;
      b.vel.y += ay * sdt;
      b.spin *= Math.max(0, 1 - table.spinDecay * sdt);
      b.captureCooldown = Math.max(0, b.captureCooldown - sdt);
      b.pos.x += b.vel.x * sdt;
      b.pos.y += b.vel.y * sdt;
    }
  };

  const resolveBounds = (): void => {
    for (const b of balls) {
      if (b.state !== "in_play") continue;

      if (b.pos.x < r) {
        b.pos.x = r;
        const impact = reflect(b, 1, 0);
        if (impact > 0) events.push({ type: "wall_hit", tick, ballId: b.id, impact });
      } else if (b.pos.x > table.width - r) {
        b.pos.x = table.width - r;
        const impact = reflect(b, -1, 0);
        if (impact > 0) events.push({ type: "wall_hit", tick, ballId: b.id, impact });
      }

      if (b.pos.y > table.length - r) {
        b.pos.y = table.length - r;
        const impact = reflect(b, 0, -1);
        if (impact > 0) events.push({ type: "wall_hit", tick, ballId: b.id, impact });
      } else if (b.pos.y <= 0) {
        // 경사 아래로 굴러 내려가 Gutter 진입 → 제거
        b.pos.y = 0;
        b.vel.x = 0;
        b.vel.y = 0;
        b.spin = 0;
        b.state = "gutter";
        events.push({ type: "gutter", tick, ballId: b.id });
      }
    }
  };

  const resolveBallCollisions = (): void => {
    const minD = r * 2;
    for (let i = 0; i < balls.length; i++) {
      const a = balls[i];
      if (a.state === "gutter") continue;
      for (let j = i + 1; j < balls.length; j++) {
        const b = balls[j];
        if (b.state === "gutter") continue;

        // gutter가 아닌 공은 모두 밀릴 수 있다. 안착한 공도 경사에 기대어 있을 뿐
        // 고정된 게 아니므로, 밀린 뒤 settleCaptured()에서 빠져나올지 판정한다.
        const d = v.dist(a.pos, b.pos);
        if (d >= minD || d === 0) continue;

        const n = { x: (b.pos.x - a.pos.x) / d, y: (b.pos.y - a.pos.y) / d };
        const overlap = minD - d;

        // 위치 보정 (겹침 제거)
        a.pos.x -= (n.x * overlap) / 2;
        a.pos.y -= (n.y * overlap) / 2;
        b.pos.x += (n.x * overlap) / 2;
        b.pos.y += (n.y * overlap) / 2;

        const sep = (b.vel.x - a.vel.x) * n.x + (b.vel.y - a.vel.y) * n.y;
        if (sep >= 0) continue; // 이미 멀어지는 중

        const imp = (-(1 + table.ballRestitution) * sep) / 2;
        a.vel.x -= imp * n.x;
        a.vel.y -= imp * n.y;
        b.vel.x += imp * n.x;
        b.vel.y += imp * n.y;

        events.push({ type: "ball_hit", tick, a: a.id, b: b.id, impact: Math.abs(sep) });
        wake(a, table.restSpeed);
        wake(b, table.restSpeed);
      }
    }
  };

  /** 원 vs 선분 충돌 해소. 겹치면 밀어내고 반사시킨다. */
  const resolveSegment = (b: SimBall, a: Vec2, c: Vec2): number => {
    const abx = c.x - a.x;
    const aby = c.y - a.y;
    const len2 = abx * abx + aby * aby;
    const t = len2 > 0
      ? Math.min(1, Math.max(0, ((b.pos.x - a.x) * abx + (b.pos.y - a.y) * aby) / len2))
      : 0;
    const px = a.x + abx * t;
    const py = a.y + aby * t;
    const dx = b.pos.x - px;
    const dy = b.pos.y - py;
    const d = Math.hypot(dx, dy);
    if (d >= r || d < 1e-9) return 0;
    const nx = dx / d;
    const ny = dy / d;
    b.pos.x = px + nx * r;
    b.pos.y = py + ny * r;
    return reflect(b, nx, ny);
  };

  /**
   * 원 vs 부채꼴 판 몸통(띠) 충돌 해소.
   *
   * 판의 실체는 {R_in ≤ |p−C| ≤ R, |α| ≤ sideAngle}인 부채 띠다
   * (α는 중심축 +y 기준 각도, 원 중심 C는 판보다 다운슬로프에 있다):
   * - 위(먼 쪽)에서 내려오면 **바깥 호**에 부딪힌다 — 법선은 반지름 바깥 방향.
   * - 아래에서 올라오면 **안쪽 호**에 부딪힌다 — 법선은 반지름 안쪽 방향.
   *   아래에서 올라오는 공을 막는 면이다.
   * - 띠 범위 밖에서는 **옆면 선분**에 부딪힌다.
   */
  const resolvePlateBody = (b: SimBall, geo: FanGeometry): number => {
    const C = geo.center;
    const dx = b.pos.x - C.x;
    const dy = b.pos.y - C.y;
    const alpha = Math.atan2(Math.abs(dx), dy); // 0 = 정면(+y), 항상 ≥ 0

    if (alpha <= geo.sideAngle) {
      const d = Math.hypot(dx, dy);
      // 띠 두께의 중간을 기준으로 가까운 면(바깥/안쪽 호)으로 밀어낸다.
      // 서브스텝이 이동을 공 반지름의 40%로 제한하므로 깊이 파고들 수 없다.
      const mid = (geo.radius + geo.innerRadius) / 2;
      if (d >= mid) {
        if (d >= geo.radius + r) return 0;
        const nx = d > 1e-9 ? dx / d : 0;
        const ny = d > 1e-9 ? dy / d : 1;
        b.pos.x = C.x + nx * (geo.radius + r);
        b.pos.y = C.y + ny * (geo.radius + r);
        return reflect(b, nx, ny);
      }
      if (d <= geo.innerRadius - r) return 0;
      const ux = d > 1e-9 ? dx / d : 0;
      const uy = d > 1e-9 ? dy / d : 1;
      b.pos.x = C.x + ux * (geo.innerRadius - r);
      b.pos.y = C.y + uy * (geo.innerRadius - r);
      // 법선은 반지름 안쪽(원 중심 쪽) 방향이다.
      return reflect(b, -ux, -uy);
    }

    // 띠 각도 밖 — 옆면 선분과 충돌.
    let impact = 0;
    for (const side of geo.sides) {
      impact = Math.max(impact, resolveSegment(b, side.a, side.b));
    }
    return impact;
  };

  /** 원 vs 페그(작은 원기둥) 충돌 해소. */
  const resolvePeg = (b: SimBall, peg: Vec2, pegR: number): number => {
    const dx = b.pos.x - peg.x;
    const dy = b.pos.y - peg.y;
    const d = Math.hypot(dx, dy);
    const minD = r + pegR;
    if (d >= minD || d < 1e-9) return 0;
    const nx = dx / d;
    const ny = dy / d;
    b.pos.x = peg.x + nx * minD;
    b.pos.y = peg.y + ny * minD;
    return reflect(b, nx, ny);
  };

  /** 판 몸통·페그에 대한 충돌. 공이 판을 통과하지 못하게 하는 핵심. */
  const resolvePlates = (): void => {
    for (const b of balls) {
      if (b.state !== "in_play") continue;

      for (const plate of plates) {
        // 구조물 근처가 아니면 건너뛴다.
        const band = plate.geometry.band;
        if (
          b.pos.x < band.minX - r ||
          b.pos.x > band.maxX + r ||
          b.pos.y < band.minY - r ||
          b.pos.y > band.maxY + r
        ) {
          continue;
        }

        let impact = resolvePlateBody(b, plate.geometry);
        for (const peg of plate.geometry.pegs) {
          impact = Math.max(impact, resolvePeg(b, peg, plate.geometry.pegRadius));
        }
        if (impact > 0) {
          events.push({
            type: "comb_hit",
            tick,
            ballId: b.id,
            astrolabe: plate.spec.id,
            impact,
          });
        }
      }
    }
  };

  const tryCapture = (): void => {
    for (const b of balls) {
      if (b.state !== "in_play") continue;
      if (b.captureCooldown > 0) continue;
      const speed = v.len(b.vel);

      for (const plate of plates) {
        const spec = plate.spec;
        // 너무 빠르면 판에 맞고 튀어나간다.
        if (speed > spec.captureSpeed) continue;

        let bestSlot = -1;
        let bestDist = Infinity;
        for (let s = 0; s < spec.slotCount; s++) {
          if (occupied.has(slotKey(spec.id, s))) continue;
          const c = plate.geometry.slots[s];
          const d = v.dist(b.pos, c);
          if (d < spec.captureRadius && d < bestDist) {
            bestDist = d;
            bestSlot = s;
          }
        }
        if (bestSlot < 0) continue;

        const c = plate.geometry.slots[bestSlot];
        b.pos.x = c.x;
        b.pos.y = c.y;
        b.vel.x = 0;
        b.vel.y = 0;
        b.spin = 0;
        b.state = "captured";
        b.astrolabe = spec.id;
        b.slot = bestSlot;
        occupied.add(slotKey(spec.id, bestSlot));
        const first = !b.scored;
        b.scored = true;
        events.push({
          type: "capture",
          tick,
          ballId: b.id,
          astrolabe: spec.id,
          slot: bestSlot,
          first,
        });
        break;
      }
    }
  };

  /**
   * 안착한 공의 뒤처리.
   *
   * 안착한 공은 고정된 게 아니라 경사에 밀려 호와 페그에 기대어 있는 상태다.
   * 다른 공에 맞아 `releaseSpeed`를 넘게 밀리면 슬롯에서 빠져나오고,
   * 그보다 약한 충격이면 제자리로 돌아간다.
   */
  const settleCaptured = (): void => {
    for (const b of balls) {
      if (b.state !== "captured") continue;
      if (b.astrolabe === null || b.slot === null) continue;

      const plate = plates.find((c) => c.spec.id === b.astrolabe);
      if (!plate) continue;

      if (v.len(b.vel) > plate.spec.releaseSpeed) {
        events.push({
          type: "release",
          tick,
          ballId: b.id,
          astrolabe: b.astrolabe,
          slot: b.slot,
        });
        occupied.delete(slotKey(b.astrolabe, b.slot));
        b.state = "in_play";
        b.astrolabe = null;
        b.slot = null;
        b.captureCooldown = RELEASE_COOLDOWN_S;
        continue;
      }

      const c = plate.geometry.slots[b.slot];
      b.pos.x = c.x;
      b.pos.y = c.y;
      b.vel.x = 0;
      b.vel.y = 0;
    }
  };

  /**
   * 아래쪽에서 뭔가가 받쳐주고 있는지.
   *
   * 경사가 균일하므로 공은 스스로 멈출 수 없다. 빗 구조물이나 이미 멈춘 공이
   * **자기보다 아래(다운슬로프)** 에서 받쳐줄 때만 멈춘다.
   */
  const isSupported = (b: SimBall): boolean => {
    for (const plate of plates) {
      const geo = plate.geometry;
      const C = geo.center;

      // 바깥 호에 기대어 있는 경우 — 법선은 반지름 방향이므로 그 y 성분을 본다.
      const dx = b.pos.x - C.x;
      const dy = b.pos.y - C.y;
      const alpha = Math.atan2(Math.abs(dx), dy);
      if (dy >= 0 && alpha <= geo.sideAngle) {
        const d = Math.hypot(dx, dy);
        if (
          d >= geo.radius &&
          d <= geo.radius + r + SUPPORT_EPS &&
          d > 1e-9 &&
          dy / d > SUPPORT_NORMAL_Y
        ) {
          return true;
        }
      }

      // 페그에 기대어 있는 경우.
      for (const peg of geo.pegs) {
        const px = b.pos.x - peg.x;
        const py = b.pos.y - peg.y;
        const d = Math.hypot(px, py);
        if (d > r + geo.pegRadius + SUPPORT_EPS || d < 1e-9) continue;
        if (py / d > SUPPORT_NORMAL_Y) return true;
      }

      // 안쪽 호는 법선이 아래를 향하고, 옆면 법선은 수평에 가깝다 — 받침이 안 된다.
    }

    for (const other of balls) {
      if (other.id === b.id) continue;
      if (other.state !== "captured" && other.state !== "grouped") continue;
      const dx = b.pos.x - other.pos.x;
      const dy = b.pos.y - other.pos.y;
      const d = Math.hypot(dx, dy);
      if (d === 0 || d > r * 2 + SUPPORT_EPS) continue;
      if (dy / d > SUPPORT_NORMAL_Y) return true;
    }

    return false;
  };

  /**
   * 정지 판정 (Grouping).
   *
   * 받쳐주는 게 없으면 절대 멈추지 않는다 — 속도가 0이 되는 순간(정점)에도
   * 중력이 즉시 다시 끌어내리므로 그대로 굴러 내려가 결국 바닥(Gutter)에 닿는다.
   */
  const settle = (): void => {
    for (const b of balls) {
      if (b.state !== "in_play") continue;
      if (v.len(b.vel) >= table.restSpeed) continue;
      if (!isSupported(b)) continue;
      b.vel.x = 0;
      b.vel.y = 0;
      b.spin = 0;
      b.state = "grouped";
      events.push({ type: "stop", tick, ballId: b.id });
    }
  };

  for (; tick < maxTicks; tick++) {
    // 이 틱에서 가장 빠른 공을 기준으로 서브스텝을 나눈다 (터널링 방지).
    let maxSpeed = 0;
    for (const b of balls) {
      if (b.state !== "in_play") continue;
      const speed = v.len(b.vel);
      if (speed > maxSpeed) maxSpeed = speed;
    }
    const maxTravel = r * SUBSTEP_TRAVEL_RATIO;
    const subSteps = Math.min(
      MAX_SUBSTEPS,
      Math.max(1, Math.ceil((maxSpeed * dt) / maxTravel)),
    );
    const sdt = dt / subSteps;

    for (let s = 0; s < subSteps; s++) {
      integrate(sdt);
      resolveBounds();
      resolveBallCollisions();
      resolvePlates();
      settleCaptured();
      tryCapture();
      settle();
    }

    // ── 리플레이 프레임 샘플링 ────────────────────────
    if (tick % sampleEvery === 0) {
      const frame: number[] = [];
      for (const b of balls) {
        if (b.state !== "in_play") continue;
        frame.push(b.id, b.pos.x, b.pos.y);
      }
      if (frame.length > 0) frames.push(frame);
    }

    if (!balls.some((b) => b.state === "in_play")) {
      tick++;
      break;
    }
  }

  if (tick >= maxTicks) {
    truncated = true;
    // 남아서 굴러다니는 공은 그 자리에서 Grouping 처리한다.
    for (const b of balls) {
      if (b.state !== "in_play") continue;
      b.vel.x = 0;
      b.vel.y = 0;
      b.spin = 0;
      b.state = "grouped";
      events.push({ type: "stop", tick, ballId: b.id });
    }
  }

  // 마지막 프레임에는 Gutter를 제외한 모든 공의 최종 위치를 담아 클라가 스냅할 수 있게 한다.
  const finalFrame: number[] = [];
  for (const b of balls) {
    if (b.state === "gutter") continue;
    finalFrame.push(b.id, b.pos.x, b.pos.y);
  }
  frames.push(finalFrame);

  return { balls, events, frames, sampleHz, ticks: tick, truncated };
}

/** grouped 상태로 멈춰 있던 공이 충격을 받아 다시 굴러가기 시작하는 처리. */
function wake(ball: SimBall, restSpeed: number): void {
  if (ball.state === "grouped" && v.len(ball.vel) > restSpeed) {
    ball.state = "in_play";
  }
}
